import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Grid,
  Paper,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import BackWithArrow from "../../common/BackWithArrow";
import DownloadIconDesign from "../../common/DownloadIconDesign";
import GlobalButton from "../../common/GlobalButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const reportData = {
  title: "Continuous Improvement Report",
  period: "Jan 2024 - Apr 2025",
  summary:
    "This report highlights the outcomes of continuous improvement efforts across various departments. It includes submissions, project completions, and KPI impacts.",
  metrics: [
    { label: "Total Ideas Submitted", value: 152 },
    { label: "Projects Completed", value: 63 },
    { label: "Ongoing Initiatives", value: 14 },
    { label: "Average Completion Time", value: "11 days" },
  ],
  highlights: [
    "High-impact project in Logistics reduced delivery delays by 40%",
    "Production team implemented 22 kaizen ideas in Q1 2025",
    "Energy consumption dropped 19% post CI efforts in March",
    "Cross-department CI task force established in Feb 2025",
  ],
};

export default function DownloadReportButton() {
  const [open, setOpen] = useState(false);

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text("Continuous Improvement Report", 10, 10);
    doc.save("CI ReportData.pdf");
  };

  return (
    <>
      <DownloadIconDesign variant="outlined" onClick={() => setOpen(true)}>
        Download Report
      </DownloadIconDesign>

      <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
        <BackWithArrow onBack={() => setOpen(false)} />

        <DialogContent
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" gutterBottom align="center">
            {reportData.title}
          </Typography>
          <Typography
            variant="subtitle1"
            gutterBottom
            color="text.secondary"
            align="center"
          >
            Reporting Period: {reportData.period}
          </Typography>

          <Typography
            variant="body1"
            sx={{ mt: 2, mb: 4, maxWidth: "800px", textAlign: "center" }}
          >
            {reportData.summary}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 4, maxWidth: "800px" }}>
            {reportData.metrics.map((metric, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    boxShadow: 3,
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {metric.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {metric.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" gutterBottom align="center">
            Highlights
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 3,
              maxWidth: "800px",
              textAlign: "left",
            }}
          >
            {reportData.highlights.map((point, idx) => (
              <li key={idx}>
                <Typography variant="body1">{point}</Typography>
              </li>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", p: 3 }}>
          <GlobalButton
            onClick={generatePDF}
            size="large"
            startIcon={<PictureAsPdfIcon />}
          >
            Download as PDF
          </GlobalButton>
          <Button
            onClick={() => setOpen(false)}
            size="large"
            color="inherit"
            variant="contained"
            sx={{
              backgroundColor: "#FFFFFF",
              color: "#4F46E5",
              "&:hover": {
                backgroundColor: "#4F46E5",
                color: "#FFFFFF",
              },
            }}
            startIcon={<RestartAltIcon color="#4F46E5" />}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
